/** The float texture a pass reads its packed rows from: a light, volume, probe or decal per row. */
import * as THREE from 'three';
/**
 * Holds `PackedRows` in a float texture of one row of texels per light, volume, probe or decal. It
 * uploads again only when it holds other rows, or when they changed.
 *
 * @canonical A float texture of packed rows
 */
export class RowTexture {
    width;
    beforeReplace;
    texture;
    /** The rows the texture holds, null before the first upload or after `invalidate`. */
    uploaded = null;
    /** `beforeReplace` gets the texture a growth is about to dispose, so the owner stops pointing at it. */
    constructor(width, beforeReplace) {
        this.width = width;
        this.beforeReplace = beforeReplace;
        this.texture = rowTexture(width, 1);
    }
    /** Marks `rows` stale, where the texture holds them, after a pack changed them in place. */
    invalidate(rows) {
        if (this.uploaded === rows)
            this.uploaded = null;
    }
    /**
     * The texture holding `rows` in its first rows. It only grows, and doubles, so a count that
     * changes as the camera culls a volume reallocates nothing. The shader reads only `count` rows.
     */
    hold(rows) {
        const height = this.texture.image.height;
        if (height < rows.count) {
            this.beforeReplace?.(this.texture);
            this.texture.dispose();
            this.texture = rowTexture(this.width, Math.max(rows.count, height * 2));
            this.uploaded = null;
        }
        if (this.uploaded !== rows) {
            const texels = this.texture.image.data;
            texels.set(rows.data.subarray(0, Math.min(texels.length, rows.count * this.width * 4)));
            this.texture.needsUpdate = true;
            this.uploaded = rows;
        }
        return this.texture;
    }
    dispose() {
        this.texture.dispose();
    }
}
function rowTexture(width, rows) {
    const texels = new Float32Array(width * 4 * rows);
    return new THREE.DataTexture(texels, width, rows, THREE.RGBAFormat, THREE.FloatType);
}
//# sourceMappingURL=rowTexture.js.map